import { pickedFile } from '../../utils/pickedFile.js'
import MediaCaptureField from '../common/MediaCaptureField.js'
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SelectField from '../common/SelectField.js';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from "../common/Loader.js";
import Alert from '../common/Alert.js';
import loanGivenService from '../../services/LoanGivenService.js';
import { Plus, Filter, ArrowLeft, Pencil, Package, MoreVertical, HandCoins, XCircle } from "lucide-react";
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js';
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';


const AddRepaymentGiven = () => {
  const { t } = useTranslation();
  const { loanId } = useParams();
  const [loan, setLoan] = useState(null);
  const [formData, setFormData] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_mode: '',
    transaction_id: '',
    remarks: ''
  });
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const { errors, validate, validateField } = useValidation();

  const validationRules = {
    amount:       (v) => validators.required(v, 'Amount') || validators.number(v, { min: 0.01, label: 'Amount' }),
    payment_date: (v) => validators.required(v, 'Payment date'),
    payment_mode: (v) => validators.select(v, 'payment mode'),
  };

  const paymentModes = [
    { value: 'cash', label: 'Cash' },
    { value: 'upi', label: 'UPI' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'cheque', label: 'Cheque' },
    { value: 'online', label: 'Online Payment' }
  ];

  useEffect(() => {
    fetchLoan();
  }, [loanId]);

  const fetchLoan = async () => {
    try {
      setLoading(true);
      const response = await loanGivenService.getById(loanId);
      const loanData = response.data;
      
      if (loanData.status !== 'approved' && loanData.status !== 'active') {
        Alert.error('Can only add repayments to approved or active loans');
        navigate('/loans-given');
        return;
      }
      
      setLoan(loanData);
      // Set default amount to pending amount
      setFormData(prev => ({
        ...prev,
        amount: loanData.amount_pending.toString()
      }));
    } catch (err) {
      Alert.error('Failed to load loan data');
      console.error(err);
      navigate('/loans-given');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let filtered = value;
    if (name === 'amount') filtered = inputFilters.amount(value);

    setFormData(prev => ({ ...prev, [name]: filtered }));

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name]);
    }
  };

  const handleScreenshotChange = (e) => {
    const file = pickedFile(e);
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('File must be less than 5MB');
        return;
      }
      
      if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
        Alert.error('Only images and PDF files are allowed');
        return;
      }
      
      setPaymentScreenshot(file);
      
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => setScreenshotPreview(reader.result);
        reader.readAsDataURL(file);
      } else {
        setScreenshotPreview('pdf');
      }
    }
  };

  const removeScreenshot = () => {
    setPaymentScreenshot(null);
    setScreenshotPreview(null);
    const fileInput = document.getElementById('screenshot-input');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate(formData, validationRules)) {
      Alert.error('Please fix the highlighted errors before submitting');
      return;
    }

    const amount = parseFloat(formData.amount);
    if (loan && amount > loan.amount_pending) {
      Alert.error(`Amount cannot exceed pending amount (${formatCurrency(loan.amount_pending)})`);
      return;
    }
    
    setSubmitting(true);

    try {
      await loanGivenService.addRepayment(loanId, formData, paymentScreenshot);
      Alert.success('Repayment added successfully!');
      setTimeout(() => navigate(`/loans-given/view/${loanId}`), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to add repayment');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const setFullPayment = () => {
    setFormData(prev => ({
      ...prev,
      amount: loan.amount_pending.toString()
    }));
  };

  const setPartialPayment = (percentage) => {
    const amount = (loan.amount_pending * percentage / 100).toFixed(2);
    setFormData(prev => ({
      ...prev,
      amount: amount
    }));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
            <Loader fullScreen={false} size="default" message="Loading loan data..." />
        </div>
      </DashboardLayout>
    );
  }

  if (!loan) return null;

  return (
    <DashboardLayout>
                {/* Header */}
                <div className="mb-4 sm:mb-6">
                  <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                    <HandCoins className="w-6 h-6 sm:w-6 sm:h-6 text-buttonBg" />Add Repayment
                  </h1>
                  <p className="text-sm sm:text-base text-gray-600">
                    Add repayment for loan: {loan.loan_id}
                  </p>
                </div>

                {/* Loan Summary Card */}
                <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6">
                  <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                    Loan Summary
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-4 mb-4">
                    <div>
                      <label className="block text-xs sm:text-sm text-gray-600 mb-1">Borrower Name</label>
                      <p className="text-sm sm:text-base font-medium text-gray-900">{loan.borrower_name}</p>
                    </div>
                    {loan.borrower_company && (
                      <div>
                        <label className="block text-xs sm:text-sm text-gray-600 mb-1">{t('common.company')}</label>
                        <p className="text-sm sm:text-base font-medium text-gray-900">{loan.borrower_company}</p>
                      </div>
                    )}
                    <div>
                      <label className="block text-xs sm:text-sm text-gray-600 mb-1">Borrower Type</label>
                      <p className="text-sm sm:text-base font-medium text-gray-900 capitalize">
                        {loan.borrower_type.replace('_', ' ')}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm text-gray-600 mb-1">Loan Type</label>
                      <p className="text-sm sm:text-base font-medium text-gray-900 capitalize">
                        {loan.loan_type.replace('_', ' ')}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm text-gray-600 mb-1">Loan Date</label>
                      <p className="text-sm sm:text-base font-medium text-gray-900">{formatDate(loan.loan_date)}</p>
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm text-gray-600 mb-1">Due Date</label>
                      <p className="text-sm sm:text-base font-medium text-gray-900">{formatDate(loan.due_date)}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                    <div className="bg-blue-50 p-3 sm:p-4 rounded-xl">
                      <label className="block text-xs text-gray-600 mb-1">Loan Amount</label>
                      <p className="text-sm sm:text-base font-bold text-blue-700">{formatCurrency(loan.loan_amount)}</p>
                    </div>
                    <div className="bg-green-50 p-3 sm:p-4 rounded-xl">
                      <label className="block text-xs text-gray-600 mb-1">Total Amount</label>
                      <p className="text-sm sm:text-base font-bold text-green-700">{formatCurrency(loan.total_amount)}</p>
                    </div>
                    <div className="bg-orange-50 p-3 sm:p-4 rounded-xl">
                      <label className="block text-xs text-gray-600 mb-1">Amount Received</label>
                      <p className="text-sm sm:text-base font-bold text-orange-700">{formatCurrency(loan.amount_paid)}</p>
                    </div>
                    <div className="bg-red-50 p-3 sm:p-4 rounded-xl">
                      <label className="block text-xs text-gray-600 mb-1">Pending</label>
                      <p className="text-sm sm:text-base font-bold text-red-700">{formatCurrency(loan.amount_pending)}</p>
                    </div>
                  </div>
                </div>

                {/* Repayment Form */}
                <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-4 sm:p-8 space-y-6">
                  {/* Quick Amount Selection */}
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-2">
                      Quick Amount Selection
                    </label>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="success" onClick={setFullPayment}>
                        Full Payment ({formatCurrency(loan.amount_pending)})
                      </Button>
                      <button
                        type="button"
                        onClick={() => setPartialPayment(50)}
                        className="px-3 py-2 bg-blue-500 text-white rounded-xl text-sm font-medium hover:bg-blue-600 transition-colors"
                      >
                        50% ({formatCurrency(loan.amount_pending * 0.5)})
                      </button>
                      <button
                        type="button"
                        onClick={() => setPartialPayment(25)}
                        className="px-3 py-2 bg-purple-500 text-white rounded-xl text-sm font-medium hover:bg-purple-600 transition-colors"
                      >
                        25% ({formatCurrency(loan.amount_pending * 0.25)})
                      </button>
                      <button
                        type="button"
                        onClick={() => setPartialPayment(10)}
                        className="px-3 py-2 bg-indigo-500 text-white rounded-xl text-sm font-medium hover:bg-indigo-600 transition-colors"
                      >
                        10% ({formatCurrency(loan.amount_pending * 0.1)})
                      </button>
                    </div>
                  </div>

                  {/* Payment Details */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Payment Details
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                          Repayment Amount *
                        </label>
                        <input
                          type="text"
                          name="amount"
                          value={formData.amount}
                          onChange={handleChange}
                          placeholder="Enter repayment amount"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.amount ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.amount} />
                        <p className="text-xs text-gray-600 mt-1">
                          Maximum: {formatCurrency(loan.amount_pending)}
                        </p>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                          Payment Date *
                        </label>
                        <input
                          type="date"
                          name="payment_date"
                          value={formData.payment_date}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.payment_date ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.payment_date} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                          Payment Mode *
                        </label>
                        <SelectField variant="form"
                          name="payment_mode"
                          value={formData.payment_mode}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.payment_mode ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        >
                          <option value="">Select Payment Mode</option>
                          {paymentModes.map((mode) => (
                            <option key={mode.value} value={mode.value}>
                              {mode.label}
                            </option>
                          ))}
                        </SelectField>
                        <ValidationMessage error={errors.payment_mode} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                          Transaction ID / Reference Number
                        </label>
                        <input
                          type="text"
                          name="transaction_id"
                          value={formData.transaction_id}
                          onChange={handleChange}
                          placeholder="Enter transaction ID"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Payment Screenshot */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Payment Proof
                    </h3>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                        Payment Screenshot / Receipt
                      </label>
                      <MediaCaptureField mode="document" onPick={handleScreenshotChange} />
                      <p className="text-xs text-gray-600 mt-1">
                        Upload payment screenshot or receipt (Max 5MB)
                      </p>
                      
                      {screenshotPreview && (
                        <div className="mt-3">
                          {screenshotPreview === 'pdf' ? (
                            <div className="flex items-center gap-2 p-3 bg-gray-100 rounded-xl">
                              <span className="text-2xl">📄</span>
                              <span className="text-sm text-gray-700">{paymentScreenshot.name}</span>
                              <button
                                type="button"
                                onClick={removeScreenshot}
                                className="ml-auto text-red-500 hover:text-red-600"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <div className="relative inline-block">
                              <img 
                                src={screenshotPreview} 
                                alt="Payment Screenshot" 
                                className="w-32 h-32 sm:w-40 sm:h-40 object-cover border-2 border-gray-300 rounded-md"
                              />
                              <button
                                type="button"
                                onClick={removeScreenshot}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-xl w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                              >
                                ✕
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Remarks */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Additional Information
                    </h3>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                        Remarks / Notes
                      </label>
                      <textarea
                        name="remarks"
                        value={formData.remarks}
                        onChange={handleChange}
                        placeholder="Enter any additional notes about this repayment"
                        rows="3"
                        className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                      />
                    </div>
                  </div>

                  {/* Summary Box */}
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                    <h4 className="font-semibold text-blue-900 mb-2">Repayment Summary</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-blue-800">Repayment Amount:</span>
                        <span className="font-semibold text-blue-900">
                          {formatCurrency(parseFloat(formData.amount) || 0)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-800">Current Pending:</span>
                        <span className="font-semibold text-blue-900">{formatCurrency(loan.amount_pending)}</span>
                      </div>
                      <div className="flex justify-between border-t border-blue-300 pt-1 mt-1">
                        <span className="text-blue-800 font-semibold">Remaining After Repayment:</span>
                        <span className="font-bold text-blue-900">
                          {formatCurrency(Math.max(0, loan.amount_pending - (parseFloat(formData.amount) || 0)))}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 sm:pt-6 border-t">
                    <Button type="submit" variant="primary" disabled={submitting} loading={submitting}>
                      {submitting ? 'Processing...' : 'Add Repayment'}
                    </Button>
                    <Button variant="secondary" onClick={() => navigate(`/loans-given/view/${loanId}`)}>
                      Cancel
                    </Button>
                  </div>
                </form>
    </DashboardLayout>
  );
};

export default AddRepaymentGiven;